import { ImageList, Theme, useMediaQuery } from '@mui/material';
import { useState } from 'react';
import { OnlyFansSubscriber } from '../../../../hooks/useSubscriber';
import { OnlyFansVaultMedias } from '../../../../hooks/useSubscriberVaultAlbums';
import useSubscriberVaultMediaReleaseForms from '../../../../hooks/useSubscriberVaultMediaReleaseForms';
import { VaultMediaResponseList } from '../../../../hooks/useSubscriberVaultMedias';
import AudioPlayerDialog from '../../../common/AudioPlayerDialog';
import MessageUsersFormAddMediasSelectorImageListItem from './MessageUsersFormAddMediasSelectorImageListItem';

type Props = {
    subscriber: OnlyFansSubscriber;
    albumMedias: VaultMediaResponseList[];
    thumbnails: any[];
    medias: OnlyFansVaultMedias.RestructuredResponseItem[];
    setMedias: React.Dispatch<React.SetStateAction<OnlyFansVaultMedias.RestructuredResponseItem[]>>;
    mediasPreviews: number[];
    setMediasPreviews: React.Dispatch<React.SetStateAction<number[]>>;
    getAudio: (url: string) => Promise<Blob | null>;
    tagCreators: boolean;
    setTagCreators: (tagCreators: boolean) => void;
    taggedCreators: { id: number; name: string }[];
    setTaggedCreators: React.Dispatch<React.SetStateAction<{ id: number; name: string }[]>>;
    taggedReleaseForms: { id: number; name: string }[];
    setTaggedReleaseForms: React.Dispatch<React.SetStateAction<{ id: number; name: string }[]>>;
    theme: Theme;
    disabled?: boolean;
};

const MessageUsersFormAddMediasSelectorImageList = (props: Props) => {
    const {
        subscriber,
        albumMedias,
        thumbnails,
        medias,
        setMedias,
        mediasPreviews,
        setMediasPreviews,
        getAudio,
        tagCreators,
        setTagCreators,
        taggedCreators,
        setTaggedCreators,
        taggedReleaseForms,
        setTaggedReleaseForms,
        theme,
        disabled,
    } = props;

    const isLargeScreen = useMediaQuery(theme.breakpoints.up('sm'));

    const [playerOpen, setPlayerOpen] = useState(false);
    const [selectedAudioUrl, setSelectedAudioUrl] = useState('');

    const { fetchVaultMediaReleaseForms } = useSubscriberVaultMediaReleaseForms();

    const handleMediaClicked = async (albumMedia: VaultMediaResponseList) => {
        if (disabled) {
            return;
        }

        if (medias.find(media => media.id === albumMedia.id)) {
            setMedias(prevMedias => {
                return prevMedias.filter(media => media.id !== albumMedia.id);
            });
        } else {
            setMedias(prevMedias => {
                return [
                    ...prevMedias,
                    {
                        id: albumMedia.id,
                        type: albumMedia.type,
                        src: albumMedia.src || '',
                        cachedThumb: albumMedia.cachedThumb,
                        createdAt: albumMedia.createdAt,
                        counters: albumMedia.counters,
                        duration: albumMedia.duration || 0,
                        hasError: albumMedia.hasError,
                        thumb: albumMedia.thumb,
                        canView: albumMedia.canView,
                    },
                ];
            });

            const taggedReleaseForms = await fetchVaultMediaReleaseForms(albumMedia.id);

            const missingMentionedUsers: { id: number; name: string }[] = [];
            const missingReleaseForms: { id: number; name: string }[] = [];

            // Does taggedCreators already have these mentionedUsers?
            if (albumMedia.mentionedUsers && albumMedia.mentionedUsers.length > 0) {
                for (let i = 0; i < albumMedia.mentionedUsers.length; i += 1) {
                    const mentionedUser = albumMedia.mentionedUsers[i];

                    if (
                        !taggedCreators.find(taggedCreator => taggedCreator.id === mentionedUser.id) &&
                        !missingMentionedUsers.find(missingMentionedUser => missingMentionedUser.id === mentionedUser.id)
                    ) {
                        missingMentionedUsers.push({ id: mentionedUser.id, name: mentionedUser.name });
                    }
                }
            }

            // Add any missing releaseFormsUsers to taggedCreators
            const releaseFromsUsers = taggedReleaseForms.filter(releaseForm => releaseForm.type === 'user' && releaseForm.user);

            if (releaseFromsUsers && releaseFromsUsers.length > 0) {
                for (let i = 0; i < releaseFromsUsers.length; i += 1) {
                    if (
                        !taggedCreators.find(taggedCreator => taggedCreator.id === releaseFromsUsers[i].user?.id) &&
                        !missingMentionedUsers.find(missingMentionedUser => missingMentionedUser.id === releaseFromsUsers[i].user?.id)
                    ) {
                        missingMentionedUsers.push({
                            id: releaseFromsUsers[i].user?.id || 0,
                            name: releaseFromsUsers[i].user?.name || '',
                        });
                    }
                }
            }

            // Only tag creators if the releaseForm is a document
            const releaseFormsDocuments =
                albumMedia.releaseForms && albumMedia.releaseForms.filter(releaseForm => releaseForm.type === 'document');

            if (releaseFormsDocuments && releaseFormsDocuments.length > 0) {
                // Does taggedReleaseForms already have these releaseFormsFiltered?
                for (let i = 0; i < releaseFormsDocuments.length; i += 1) {
                    if (
                        !taggedReleaseForms.find(taggedReleaseForm => taggedReleaseForm.id === releaseFormsDocuments[i].id) &&
                        !missingReleaseForms.find(missingReleaseForm => missingReleaseForm.id === releaseFormsDocuments[i].id)
                    ) {
                        missingReleaseForms.push({ id: releaseFormsDocuments[i].id, name: releaseFormsDocuments[i].name });
                    }
                }
            }

            if (!tagCreators && (missingMentionedUsers.length > 0 || missingReleaseForms.length > 0)) {
                setTagCreators(true);
            }

            // Add the missing mentionedUsers to taggedCreators
            if (missingMentionedUsers.length > 0) {
                setTaggedCreators(prevTaggedCreators => {
                    return [...prevTaggedCreators, ...missingMentionedUsers];
                });
            }

            // Add the missing releaseFormsFiltered to taggedReleaseForms
            if (missingReleaseForms.length > 0) {
                setTaggedReleaseForms(prevTaggedReleaseForms => {
                    return [...prevTaggedReleaseForms, ...missingReleaseForms];
                });
            }
        }

        if (mediasPreviews.includes(albumMedia.id)) {
            setMediasPreviews(prevMediasPreviews => {
                return prevMediasPreviews.filter(mediaPreview => mediaPreview !== albumMedia.id);
            });
        }
    };

    return (
        <>
            <ImageList cols={isLargeScreen ? 4 : 3} rowHeight={isLargeScreen ? 166 : 122} gap={0} style={{ width: '100%' }}>
                {albumMedias
                    .filter(item => !item.hasError && item.canView)
                    .map((item, index) => (
                        <MessageUsersFormAddMediasSelectorImageListItem
                            key={item.id}
                            subscriber={subscriber}
                            medias={medias}
                            item={item}
                            thumbnails={thumbnails}
                            handleMediaClicked={handleMediaClicked}
                            setSelectedAudioUrl={setSelectedAudioUrl}
                            setPlayerOpen={setPlayerOpen}
                            isLargeScreen={isLargeScreen}
                            disabled={disabled}
                        />
                    ))}
            </ImageList>
            <AudioPlayerDialog open={playerOpen} setOpen={setPlayerOpen} url={selectedAudioUrl} getAudio={getAudio} />
        </>
    );
};

export default MessageUsersFormAddMediasSelectorImageList;
